import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, CheckCircle2, Monitor, Smartphone, Globe, Download } from 'lucide-react'

// ─── FAQ Accordion ────────────────────────────────────────────────
function FAQItem({ q, a, isOpen, toggle }) {
  return (
    <div className="border border-border/60 rounded-xl overflow-hidden bg-card/50">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-sm sm:text-base hover:bg-primary/5 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-foreground">{q}</span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border/40 pt-3">
          {a}
        </div>
      )}
    </div>
  )
}

// ─── Section heading helper ───────────────────────────────────────
function SectionHeading({ children }) {
  return (
    <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-5 pb-3 border-b border-border/50">
      {children}
    </h2>
  )
}

// ─── Main component ───────────────────────────────────────────────
export default function DownloaderSEO({ provider }) {
  const [openFAQ, setOpenFAQ] = useState(null)
  const toggle = (i) => setOpenFAQ(prev => prev === i ? null : i)

  const content = CONTENT[provider] || CONTENT.youtube

  return (
    <div className="space-y-14 text-sm sm:text-base">

      {/* ── How To ──────────────────────────────────────────────── */}
      <section aria-labelledby="howto-heading">
        <SectionHeading>{content.howToTitle}</SectionHeading>
        <p className="text-muted-foreground leading-relaxed mb-6">{content.intro}</p>
        <ol className="space-y-3">
          {content.steps.map((step, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center border border-primary/20">
                {i + 1}
              </span>
              <div className="pt-0.5">
                <span className="font-semibold text-foreground">{step.title}: </span>
                <span className="text-muted-foreground">{step.text}</span>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Overview ────────────────────────────────────────────── */}
      <section aria-labelledby="overview-heading">
        <SectionHeading>Overview</SectionHeading>
        <p className="text-muted-foreground leading-relaxed mb-5">{content.overviewIntro}</p>
        <ul className="space-y-2.5">
          {content.overviewPoints.map((point, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground leading-relaxed">{point}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Devices ─────────────────────────────────────────────── */}
      <section aria-labelledby="devices-heading">
        <SectionHeading>Devices &amp; Systems Supported</SectionHeading>
        <p className="text-muted-foreground leading-relaxed mb-5">{content.devicesText}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Monitor,    label: 'Desktop',   detail: 'Windows, macOS, Linux' },
            { icon: Smartphone, label: 'Mobile',    detail: 'Android &amp; iOS' },
            { icon: Globe,      label: 'Browsers',  detail: 'Chrome, Firefox, Safari, Edge' },
          ].map(({ icon: Icon, label, detail }) => (
            <div key={label} className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border/50">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">{label}</p>
                <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: detail }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section aria-labelledby="faq-heading">
        <SectionHeading>Frequently Asked Questions</SectionHeading>
        <div className="space-y-2">
          {content.faqs.map(({ q, a }, i) => (
            <FAQItem
              key={i}
              q={q}
              a={a}
              isOpen={openFAQ === i}
              toggle={() => toggle(i)}
            />
          ))}
        </div>
      </section>

      {/* ── Related Tools ────────────────────────────────────────── */}
      <section aria-labelledby="related-heading" className="bg-card/50 rounded-2xl border border-border/50 p-6">
        <SectionHeading>Related Downloaders &amp; Tools</SectionHeading>
        <p className="text-muted-foreground mb-5 text-sm">
          FastAudio supports all major video platforms. Explore our other free downloaders:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {content.relatedLinks.map(({ href, label, desc }) => (
            <Link
              key={href}
              to={href}
              className="flex items-start gap-3 p-4 bg-background rounded-xl border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all group"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Download className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Per-provider content
// ─────────────────────────────────────────────────────────────────
const CONTENT = {

  // ── YouTube ──────────────────────────────────────────────────
  youtube: {
    howToTitle: 'How to Convert YouTube Videos to MP3',
    intro:
      'FastAudio makes it simple to save any YouTube video as a high-quality MP3 audio file. No software installation, no account required — just paste, select your quality, and download in seconds.',
    steps: [
      { title: 'Copy the YouTube URL',   text: 'Open the YouTube video you want and copy the URL from your browser address bar or tap "Share → Copy link" in the YouTube app.' },
      { title: 'Paste into FastAudio',   text: 'Click the search box above, paste the URL using Ctrl+V (or tap Paste), and wait a moment while we fetch the video details.' },
      { title: 'Select audio quality',   text: 'Choose MP3 128kbps for smaller files, 192kbps for a good balance, or 320kbps for the highest audio quality — perfect for music.' },
      { title: 'Click Convert Media',    text: 'Hit the green "Convert Media" button. Our servers will process the audio extraction instantly.' },
      { title: 'Download your MP3',      text: 'Once the conversion is complete, click the Download button. The MP3 file will save directly to your device.' },
    ],
    overviewIntro:
      'FastAudio is a powerful YouTube to MP3 converter that works entirely in your browser — no downloads, no sign-ups, no ads.',
    overviewPoints: [
      'Supports all YouTube video types: standard videos, YouTube Shorts, YouTube Music, and live recordings.',
      'Three audio quality options: 128kbps (standard), 192kbps (high), and 320kbps (maximum) — you choose what suits your needs.',
      'No third-party software or browser extensions required — works on any device with a browser.',
      'Files are processed on our secure servers and never stored permanently — your privacy is protected.',
      'Completely free with no download limits, no account required, and no annoying popups.',
      'Fast conversion speeds powered by cloud servers — most videos convert in under 30 seconds.',
    ],
    devicesText:
      'FastAudio works on every modern device and operating system. Whether you\'re on a Windows PC, a MacBook, an Android phone, or an iPhone, you can convert YouTube to MP3 directly in your browser with no app installation needed.',
    faqs: [
      {
        q: 'Can I download YouTube Shorts to MP3?',
        a: 'Yes! FastAudio fully supports YouTube Shorts. Just copy the Shorts URL (e.g. youtube.com/shorts/...) and paste it into the converter — it works exactly the same as any regular YouTube video.',
      },
      {
        q: 'What is the best MP3 quality to choose?',
        a: 'For music, we recommend 320kbps — it\'s the highest quality and produces audio indistinguishable from the original. For podcasts or spoken word content, 128kbps is sufficient and creates a much smaller file.',
      },
      {
        q: 'Is FastAudio completely free?',
        a: 'Yes, FastAudio is 100% free with no hidden charges. There are no download limits, no registration walls, and no premium plans — every feature is available to every user at no cost.',
      },
      {
        q: 'Does it work on mobile and tablet?',
        a: 'Absolutely. FastAudio is fully responsive and works in all modern mobile browsers including Chrome on Android and Safari on iPhone. No app download is required.',
      },
      {
        q: 'Why does my YouTube download sometimes fail?',
        a: 'YouTube occasionally flags automated requests from cloud servers. If a download fails, try again — it usually succeeds on the second attempt. Age-restricted videos, private videos, and some region-locked content cannot be downloaded.',
      },
      {
        q: 'Can I download an entire YouTube playlist?',
        a: 'FastAudio converts individual videos. For playlists, copy the URL of each video you want to download separately. Batch/playlist downloading is not currently supported.',
      },
      {
        q: 'Does FastAudio support YouTube Music?',
        a: 'Yes! YouTube Music URLs (music.youtube.com/watch?v=...) are fully supported. You can download any publicly available YouTube Music track as an MP3.',
      },
    ],
    relatedLinks: [
      { href: '/youtube-video-downloader',   label: 'YouTube Video Downloader',      desc: 'Download YouTube videos as MP4 in 480p, 720p, or 1080p Full HD.' },
      { href: '/instagram-downloader',        label: 'Instagram to MP3',              desc: 'Extract audio from Instagram Reels and video posts.' },
      { href: '/tiktok-downloader',           label: 'TikTok to MP3',                 desc: 'Download TikTok audio as a clean, watermark-free MP3.' },
      { href: '/facebook-downloader',         label: 'Facebook to MP3 & MP4',         desc: 'Save audio or video from any public Facebook post.' },
    ],
  },

  // ── Instagram ────────────────────────────────────────────────
  instagram: {
    howToTitle: 'How to Download Instagram Reels & Videos',
    intro:
      'FastAudio lets you save the audio from any public Instagram Reel, post, or IGTV video as an MP3 file — straight from your browser, no account needed.',
    steps: [
      { title: 'Find the Instagram post',  text: 'Open Instagram and navigate to the Reel, post, or IGTV video you want to save.' },
      { title: 'Copy the link',            text: 'Tap the three-dot (⋯) menu on the post and select "Copy Link". On desktop, copy the URL from your browser\'s address bar.' },
      { title: 'Paste into FastAudio',     text: 'Click the search box above and paste the Instagram URL. The video details will load automatically.' },
      { title: 'Select your quality',      text: 'Choose your preferred MP3 audio quality — 128kbps, 192kbps, or 320kbps — from the options shown.' },
      { title: 'Convert and download',     text: 'Click "Convert Media" and wait for the conversion. Your MP3 file will be ready to download in seconds.' },
    ],
    overviewIntro:
      'FastAudio\'s Instagram downloader extracts audio from public Instagram content quickly and securely — no login, no extension, no app required.',
    overviewPoints: [
      'Supports Instagram Reels, standard feed posts with video, and IGTV videos.',
      'Downloads audio as high-quality MP3 up to 320kbps — ideal for music Reels and audio clips.',
      'Works on desktop and mobile browsers — no Instagram account required to download public content.',
      'No watermarks, no branding overlays — just clean audio from the original source.',
      'Fast processing with our cloud-based converter, typically completing in under 20 seconds.',
      'Completely free with unlimited downloads and no sign-up required.',
    ],
    devicesText:
      'FastAudio\'s Instagram downloader works on all devices. Download Instagram Reels directly from your iPhone Safari browser, Android Chrome, or any desktop browser — no app installation or Instagram login required.',
    faqs: [
      {
        q: 'What types of Instagram content can I download?',
        a: 'FastAudio supports public Instagram Reels, standard video posts, and IGTV videos. Content must be publicly accessible — private profiles, Stories (which expire), and login-protected posts cannot be downloaded.',
      },
      {
        q: 'Why does my Instagram download fail?',
        a: 'Instagram requires login to access most content when accessed from server IPs. If your download fails, the post may be private, require a login to view, or the link may have expired. Only publicly accessible posts without a login can be downloaded.',
      },
      {
        q: 'Can I download Instagram Stories?',
        a: 'No. Instagram Stories expire after 24 hours and require login to view, which means they cannot be downloaded through FastAudio.',
      },
      {
        q: 'Can I download Instagram photos?',
        a: 'FastAudio is an audio converter — it\'s designed for video and audio content. Photo posts do not contain audio and cannot be downloaded through FastAudio.',
      },
      {
        q: 'Do I need an Instagram account to use FastAudio?',
        a: 'No account is required. FastAudio can access publicly visible Instagram content without you logging in.',
      },
      {
        q: 'Is downloading Instagram audio legal?',
        a: 'Downloading content for personal, non-commercial use is generally accepted, but always respect intellectual property rights. Do not republish or redistribute downloaded content without permission from the original creator.',
      },
    ],
    relatedLinks: [
      { href: '/instagram-video-downloader', label: 'Download Instagram Reels as MP4', desc: 'Save Instagram Reels and video posts as MP4 with a single click.' },
      { href: '/youtube-downloader',          label: 'YouTube to MP3',                  desc: 'Convert any YouTube video to a high-quality MP3 audio file.' },
      { href: '/tiktok-downloader',           label: 'TikTok to MP3',                   desc: 'Download TikTok audio as a clean, watermark-free MP3.' },
      { href: '/facebook-downloader',         label: 'Facebook to MP3 & MP4',           desc: 'Save audio or video from any public Facebook post.' },
    ],
  },

  // ── TikTok ───────────────────────────────────────────────────
  tiktok: {
    howToTitle: 'How to Download TikTok Audio to MP3',
    intro:
      'FastAudio extracts the audio from any public TikTok video as a clean MP3 file — no watermarks, no TikTok branding, and no software to install.',
    steps: [
      { title: 'Open the TikTok video',  text: 'Find the TikTok video whose audio you want to save in the TikTok app or on tiktok.com.' },
      { title: 'Copy the link',          text: 'Tap the Share button (arrow icon) and select "Copy Link". On desktop, copy the URL from your browser.' },
      { title: 'Paste into FastAudio',   text: 'Paste the TikTok URL into the search box above. FastAudio will automatically detect the video and show you its details.' },
      { title: 'Select audio quality',   text: 'Pick your preferred quality — 128kbps, 192kbps, or 320kbps MP3.' },
      { title: 'Convert and save',       text: 'Click "Convert Media" to start the extraction. Your watermark-free MP3 will be ready to download in moments.' },
    ],
    overviewIntro:
      'FastAudio\'s TikTok to MP3 converter gives you a fast, clean way to save TikTok audio — without watermarks, without the app, and without paying anything.',
    overviewPoints: [
      'Extracts original audio without the TikTok watermark or any overlays.',
      'Supports full TikTok video URLs and short links (vm.tiktok.com, vt.tiktok.com).',
      'Works on iOS Safari, Android Chrome, and all desktop browsers — no app required.',
      'Three audio quality options: 128, 192, and 320kbps MP3.',
      'No TikTok account needed to download public videos.',
      'Fast cloud-based conversion — most TikTok videos convert in under 15 seconds.',
    ],
    devicesText:
      'FastAudio works seamlessly on every platform. Download TikTok audio directly from your iPhone, Android smartphone, tablet, or PC — no TikTok app login required, and no extra software to install.',
    faqs: [
      {
        q: 'Is the downloaded TikTok MP3 watermark-free?',
        a: 'Yes. FastAudio extracts the original audio stream from the TikTok video, which does not contain the visual watermark. Your MP3 file will be clean audio without any TikTok branding.',
      },
      {
        q: 'Why might a TikTok download fail?',
        a: 'TikTok blocks requests from some cloud server IP addresses, which can cause failures. Private accounts, duets from private creators, and deleted videos also cannot be downloaded. If a download fails, try again — it often succeeds on a second attempt.',
      },
      {
        q: 'Does FastAudio support vm.tiktok.com short links?',
        a: 'Yes, both standard TikTok URLs (tiktok.com/@user/video/...) and short links (vm.tiktok.com/..., vt.tiktok.com/...) are fully supported.',
      },
      {
        q: 'Can I download a TikTok video (not just audio)?',
        a: 'FastAudio is primarily an audio converter. Video download support depends on what the server is able to extract from the TikTok post.',
      },
      {
        q: 'Can I download TikTok music to use in my own videos?',
        a: 'You can download the audio for personal listening. However, using copyrighted music in your own published content may violate copyright law and TikTok\'s terms of service — always check the rights status of the audio first.',
      },
      {
        q: 'Does it work on TikTok Lite URLs?',
        a: 'Standard TikTok URLs are supported. TikTok Lite app share links may redirect to the same URLs and should work, but support is not guaranteed for all formats.',
      },
    ],
    relatedLinks: [
      { href: '/tiktok-video-downloader',    label: 'TikTok Video Downloader',        desc: 'Download TikTok videos as MP4 without any watermark.' },
      { href: '/youtube-downloader',          label: 'YouTube to MP3',                 desc: 'Convert any YouTube video to a high-quality MP3 audio file.' },
      { href: '/instagram-downloader',        label: 'Instagram to MP3',               desc: 'Extract audio from Instagram Reels and video posts.' },
      { href: '/facebook-downloader',         label: 'Facebook to MP3 & MP4',          desc: 'Save audio or video from any public Facebook post.' },
    ],
  },

  // ── Facebook ─────────────────────────────────────────────────
  facebook: {
    howToTitle: 'How to Download Facebook Videos to MP3 & MP4',
    intro:
      'FastAudio lets you save audio or video from any public Facebook video — from the Facebook app or website — directly to your device in seconds.',
    steps: [
      { title: 'Find the Facebook video', text: 'Open Facebook and locate the video post or Reel you want to download.' },
      { title: 'Copy the video URL',      text: 'Click the three-dot (⋯) menu on the post and select "Copy link". On mobile, tap Share → Copy link.' },
      { title: 'Paste into FastAudio',    text: 'Paste the Facebook URL into the search box above. FastAudio will fetch the video details automatically.' },
      { title: 'Choose your format',      text: 'Select MP3 for audio extraction (128–320kbps) or MP4 for video download (480p or 720p HD).' },
      { title: 'Download your file',      text: 'Click "Convert Media" and your file will be ready to download within seconds.' },
    ],
    overviewIntro:
      'FastAudio\'s Facebook video downloader supports MP3 audio extraction and MP4 video downloads — no Facebook account required and no browser extensions to install.',
    overviewPoints: [
      'Supports Facebook Watch videos, page video posts, and Facebook Reels.',
      'Download as MP3 audio (up to 320kbps) or MP4 video (SD 480p or HD 720p).',
      'Works with facebook.com, m.facebook.com, and fb.watch short links.',
      'No Facebook login required for publicly accessible videos.',
      'Fast server-side processing — most Facebook videos convert in under 15 seconds.',
      'Safe and private — no files are stored on our servers after download.',
    ],
    devicesText:
      'FastAudio\'s Facebook downloader works on every device. Save Facebook videos from your iPhone, iPad, Android phone, or any desktop computer — no additional apps or extensions required.',
    faqs: [
      {
        q: 'What types of Facebook videos can I download?',
        a: 'FastAudio supports standard Facebook video posts, Facebook Watch videos, and Facebook Reels that are publicly accessible without login. Private videos, group videos with restricted access, and Facebook Stories cannot be downloaded.',
      },
      {
        q: 'How do I get the correct Facebook video URL?',
        a: 'On the video post, click the three-dot (⋯) menu → Copy link. On mobile, tap Share → Copy link. You can also copy the URL directly from your browser\'s address bar when the video is open.',
      },
      {
        q: 'Can I download Facebook Reels?',
        a: 'Yes! Facebook Reels that are publicly accessible can be downloaded as MP3 audio or MP4 video through FastAudio.',
      },
      {
        q: 'Why does the thumbnail not appear for Facebook videos?',
        a: 'Facebook video thumbnails are hosted on a CDN that requires an authenticated session to display. This is a Facebook limitation — it does not affect the audio or video download itself.',
      },
      {
        q: 'Can I download private Facebook videos?',
        a: 'No. Only videos that are publicly visible without a Facebook login can be downloaded. Private videos, friend-only posts, and group-restricted content cannot be accessed by FastAudio.',
      },
      {
        q: 'Is downloading Facebook videos legal?',
        a: 'Downloading public Facebook content for personal, offline viewing is generally accepted. However, redistribution or commercial use of downloaded content may violate Facebook\'s Terms of Service and copyright law. Always obtain permission from the content creator before sharing downloaded material.',
      },
    ],
    relatedLinks: [
      { href: '/facebook-video-downloader',  label: 'Facebook Video Downloader',      desc: 'Download Facebook videos and Reels as MP4 in SD or HD.' },
      { href: '/youtube-downloader',          label: 'YouTube to MP3',                 desc: 'Convert any YouTube video to a high-quality MP3 audio file.' },
      { href: '/instagram-downloader',        label: 'Instagram to MP3',               desc: 'Extract audio from Instagram Reels and video posts.' },
      { href: '/tiktok-downloader',           label: 'TikTok to MP3',                  desc: 'Download TikTok audio as a clean, watermark-free MP3.' },
    ],
  },
}
